/** Debug overlay displaying the current danger percentage derived from nearby alert enemies. */
import Phaser from 'phaser';
import { Enemy } from '../../entities/Enemy';
import { calculateDanger } from '../../systems/DangerCalculator';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { crispText, crispRect } from '../../ui/CrispUtil';
import { TextStyle, Color, DEBUG_PANEL_BG_ALPHA } from '../../ui/Theme';
import { SF0_X, SF0_Y } from '../../utils/Constants';

// Align to CombatHUD underlay left edge (HUD_X=12, HUD_PAD=6 → left edge at 6)
const PANEL_X = SF0_X + 6;
// Position below the CombatHUD underlay (extends to y=89 after attunement icons)
const PANEL_Y = SF0_Y + 95;
// Match CombatHUD underlay width (HP_BAR_WIDTH=120 + HUD_PAD*2=12 → 132)
const PANEL_WIDTH = 132;
const PANEL_HEIGHT = 20;

/** Pooled panel showing the calculated danger value as a color-coded percentage. Reuses objects to avoid GPU texture churn. */
export class DangerOverlay {
  private scene: Phaser.Scene;
  private camera: Phaser.Cameras.Scene2D.Camera;
  private label: Phaser.GameObjects.Text | null = null;
  private bg: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.camera = scene.cameras.main;
  }

  /** Recalculate and update the danger panel for the current frame. */
  draw(_graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    const xpConfig = ConfigLoader.xpGainConfig;
    const danger = calculateDanger(
      enemies, this.camera,
      xpConfig.dangerMaxEnemies, xpConfig.dangerPresenceBase,
      xpConfig.dangerDistanceRadius, xpConfig.dangerDistanceFloor,
      xpConfig.dangerSigmoidSteepness, xpConfig.dangerSigmoidMidpoint,
    );

    const pct = Math.round(danger * 100);
    let color: string;
    if (danger >= 0.75) color = '#cc2222';
    else if (danger >= 0.5) color = '#cc8822';
    else if (danger >= 0.25) color = '#cccc22';
    else color = '#22cc22';

    if (!this.bg) {
      this.bg = crispRect(this.scene,
        PANEL_X + PANEL_WIDTH / 2, PANEL_Y + PANEL_HEIGHT / 2,
        PANEL_WIDTH, PANEL_HEIGHT, Color.black, DEBUG_PANEL_BG_ALPHA,
      ).setScrollFactor(0).setDepth(2000);
    }
    this.bg.setVisible(true);

    if (!this.label) {
      this.label = crispText(this.scene, PANEL_X + 6, PANEL_Y + 4, '', {
        ...TextStyle.debugPanel, color,
      }).setScrollFactor(0).setDepth(2001);
    }
    const dangerStr = `DANGER: ${pct}%`;
    if (this.label.text !== dangerStr) this.label.setText(dangerStr);
    if (this.label.style.color !== color) this.label.setColor(color);
    this.label.setVisible(true);
  }

  /** Hide the label and background. */
  clear(): void {
    if (this.label) this.label.setVisible(false);
    if (this.bg) this.bg.setVisible(false);
  }

  /** Destroy all objects and free resources. */
  destroy(): void {
    if (this.label) {
      this.label.destroy();
      this.label = null;
    }
    if (this.bg) {
      this.bg.destroy();
      this.bg = null;
    }
  }
}
